<script lang="ts" setup>
import { computed, nextTick, ref, useId, watch } from 'vue'
import { useElementBounding } from '@vueuse/core'
import { vOnClickOutside } from '@vueuse/components'
import MaterialSymbolsCheckRounded from '~icons/material-symbols/check-rounded'
import PhInfo from '~icons/ph/info'
import BProgress from './BProgress.vue'
import { bgColors } from '@/services/application/constantes'
import { size as sizeConst } from '@/services/application/constantes'

export type IOption = {
  _id: string | number | null | undefined
  display: string
  value?: string | number | boolean | null
  /** Shown in the list but not selectable (e.g. a feature of an option the firm did not subscribe) */
  disabled?: boolean
}

const props = withDefaults(defineProps<{
  disabled?: boolean
  rounded?: string
  popoverContainer?: any
  label?: string
  /** Shown in an info icon next to the label */
  labelTooltip?: string
  bg?: keyof typeof bgColors
  dropdownBg?: keyof typeof bgColors
  options: IOption[]
  tabindex?: number
  modelValue?: IOption | string | number
  forceId?: boolean
  loading?: boolean
  size?: keyof typeof sizeConst
  required?: boolean
  isError?: boolean
  placeholderTranslationKey?: string
  openScrollToIndex?: number
}>(), {
  options: [],
  tabindex: 0,
  bg: 'base',
  dropdownBg: 'base',
  rounded: 'rounded-lg',
  disabled: false,
  size: 'small',
  loading: false,
  forceId: false,
  label: '',
  popoverContainer: 'body',
  required: false,
  isError: false,
  placeholderTranslationKey: 'common.selectElement',
  openScrollToIndex: undefined,
})
const emit = defineEmits<{
  selected: [option: IOption]
  'update:modelValue': [value: IOption['_id'] | IOption]
}>()

const oontainerRef = ref()
const optionsContainerRef = ref<HTMLElement | null>(null)
const triggerRef = ref<HTMLElement | null>(null)
const { width } = useElementBounding(oontainerRef)

const labelId = useId()
const listboxId = useId()

const open = ref(false)
const activeIndex = ref(-1)

const optionSelected = computed(() => {
  // Object mode: resolve by _id, including null / undefined sentinels
  if (typeof props.modelValue === 'object' && props.modelValue !== null)
    return props.options.find(option => option._id === props.modelValue._id) || null

  // Id mode: allow 0 and null as valid selected ids
  if (props.modelValue === undefined || props.modelValue === '')
    return null

  return props.options.find(option => option._id === props.modelValue) || null
})

const selectedOptionIndex = computed(() =>
  props.options.findIndex(option => option._id === optionSelected.value?._id),
)

const isOptionSelected = (option: IOption) => option._id === optionSelected.value?._id

const scrollOptionsToIndex = () => {
  if (props.openScrollToIndex === undefined)
    return

  const optionsContainer = optionsContainerRef.value
  if (!optionsContainer)
    return

  const targetOption = optionsContainer.children.item(Math.max(props.openScrollToIndex, 0)) as HTMLElement | null

  if (!targetOption)
    return

  optionsContainer.scrollTop = targetOption.offsetTop
}

const queueScrollOptionsToIndex = async () => {
  if (!open.value || props.openScrollToIndex === undefined)
    return

  await nextTick()
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      scrollOptionsToIndex()
    })
  })
}

const openSelect = () => {
  if (props.disabled || props.options.length < 1)
    return

  open.value = !open.value
  if (open.value) {
    const selectedIndex = selectedOptionIndex.value
    activeIndex.value = selectedIndex >= 0 ? selectedIndex : 0
    nextTick(() => focusOption(activeIndex.value))
  }
  else {
    activeIndex.value = -1
  }
}

const closeSelect = () => {
  open.value = false
  activeIndex.value = -1
}

const selectItem = (option: IOption) => {
  if (option?.disabled)
    return
  closeSelect()
  const shouldEmitId = typeof props.modelValue === 'string' || typeof props.modelValue === 'number' || props.forceId
  // Never emit a bare undefined _id — it clears v-model and crashes object-mode consumers
  const updateToSend = shouldEmitId && option._id !== undefined ? option._id : option
  emit('selected', option)
  emit('update:modelValue', updateToSend)
  nextTick(() => triggerRef.value?.focus())
}

const getOptionDataCyId = (option: IOption) => {
  if (option._id === undefined)
    return 'undefined'
  if (option._id === null)
    return 'null'

  return `${option._id}`
}

const focusOption = async (index: number) => {
  activeIndex.value = index
  await nextTick()
  const el = optionsContainerRef.value?.children.item(index) as HTMLElement | null
  el?.focus()
  el?.scrollIntoView({ block: 'nearest' })
}

const onTriggerKeydown = (event: KeyboardEvent) => {
  if (props.disabled || props.options.length < 1)
    return

  switch (event.key) {
    case 'Enter':
    case ' ':
    case 'ArrowDown':
    case 'ArrowUp':
      event.preventDefault()
      if (!open.value) {
        open.value = true
        const selectedIndex = selectedOptionIndex.value
        activeIndex.value = selectedIndex >= 0 ? selectedIndex : 0
        nextTick(() => focusOption(activeIndex.value))
      }
      else if (event.key === 'ArrowDown') {
        const next = Math.min(activeIndex.value + 1, props.options.length - 1)
        focusOption(next)
      }
      else if (event.key === 'ArrowUp') {
        const prev = Math.max(activeIndex.value - 1, 0)
        focusOption(prev)
      }
      else if ((event.key === 'Enter' || event.key === ' ') && activeIndex.value >= 0) {
        const option = props.options[activeIndex.value]
        if (option)
          selectItem(option)
      }
      break
    case 'Escape':
      if (open.value) {
        event.preventDefault()
        event.stopPropagation()
        closeSelect()
      }
      break
  }
}

const onOptionKeydown = (event: KeyboardEvent, option: IOption, index: number) => {
  switch (event.key) {
    case 'Enter':
    case ' ':
      event.preventDefault()
      selectItem(option)
      break
    case 'ArrowDown':
      event.preventDefault()
      focusOption(Math.min(index + 1, props.options.length - 1))
      break
    case 'ArrowUp':
      event.preventDefault()
      focusOption(Math.max(index - 1, 0))
      break
    case 'Escape':
      event.preventDefault()
      event.stopPropagation()
      closeSelect()
      nextTick(() => triggerRef.value?.focus())
      break
    case 'Tab':
      closeSelect()
      break
  }
}

watch(open, (value) => {
  if (value)
    void queueScrollOptionsToIndex()
})
</script>

<template>
  <div ref="oontainerRef" class="flex flex-col relative" :class="{ 'mt-5': label }">
    <slot name="label">
      <p
        v-if="label"
        :id="labelId"
        class="subtitle absolute -top-2 left-0 transform -translate-y-1/2"
        :class="sizeConst[size]"
      >
        {{ label }}
        <span v-if="required" class="p-1 text-danger-400" aria-hidden="true">*</span>
        <span v-if="labelTooltip" v-tooltip="labelTooltip" tabindex="0" class="inline-flex align-middle cursor-help"><PhInfo /></span>
      </p>
    </slot>
    <div
      ref="triggerRef"
      role="combobox"
      :tabindex="disabled || options.length < 1 ? -1 : tabindex"
      :aria-expanded="open"
      aria-haspopup="listbox"
      :aria-controls="listboxId"
      :aria-labelledby="label ? labelId : undefined"
      :aria-required="required || undefined"
      :aria-invalid="isError || undefined"
      :aria-busy="loading || undefined"
      :aria-disabled="disabled || options.length < 1 || undefined"
      class="selected custom-select flex border border-base pl-3 pr-5 relative items-center focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-hidden"
      :class="[bgColors[bg], sizeConst[size], { 'open': open, 'pointer-events-none opacity-50': options.length < 1, 'opacity-50 cursor-not-allowed!': disabled, 'bg-danger-400 text-white': isError }, rounded]"
      :data-cy-selected-option-id="optionSelected ? getOptionDataCyId(optionSelected) : undefined"
      @click="openSelect"
      @keydown="onTriggerKeydown"
    >
      <slot v-if="optionSelected" name="selected" :selected="optionSelected">
        <p class="line-clamp-1">
          {{ optionSelected.display }}
        </p>
      </slot>
      <slot v-else-if="options.length < 1" name="noData">
        <p class="line-clamp-1">
          {{ $t('common.selectNoData') }}
        </p>
      </slot>
      <slot v-else name="placeholder">
        <p class="line-clamp-1">
          {{ $t(placeholderTranslationKey) }}
        </p>
      </slot>
    </div>
    <VDropdown :container="popoverContainer" :triggers="[]" :shown="open" :auto-hide="false" placement="bottom">
      <template #popper>
        <div
          :id="listboxId"
          ref="optionsContainerRef"
          v-on-click-outside="() => closeSelect()"
          role="listbox"
          :aria-labelledby="label ? labelId : undefined"
          :style="{ width: `${width}px` }"
          class="max-h-56 overflow-x-hidden overflow-y-auto transition-all mini-scroll rounded-lg"
          :class="[bgColors[dropdownBg]]"
        >
          <div
            v-for="(option, i) of options"
            :key="option._id ?? `option-${i}`"
            role="option"
            tabindex="-1"
            :aria-selected="isOptionSelected(option)"
            :aria-disabled="option?.disabled || undefined"
            :data-cy="`select-item-${i}`"
            :data-cy-option-id="getOptionDataCyId(option)"
            class="item flex items-center justify-between gap-2 px-2 py-2 cursor-pointer select-none hover:bg-fade focus:bg-fade focus:outline-hidden"
            :class="{ 'bg-fade': isOptionSelected(option), 'opacity-50 cursor-not-allowed!': option?.disabled }"
            @click="selectItem(option)"
            @keydown="onOptionKeydown($event, option, i)"
          >
            <slot name="option" :option="option" :selected="isOptionSelected(option)">
              {{ option?.display || option }}
            </slot>
            <MaterialSymbolsCheckRounded
              v-if="isOptionSelected(option)"
              class="w-4 h-4 shrink-0 opacity-60"
              aria-hidden="true"
            />
          </div>
        </div>
      </template>
    </VDropdown>
    <BProgress v-show="loading" color="bg-primary-400" bg="bg-primary-100" height="h-1" :loading="loading" />
  </div>
</template>

<style>
.custom-select {
  position: relative;
  text-align: left;
}

.selected {
  cursor: pointer;
  user-select: none;
}

.selected:after {
  position: absolute;
  content: "";
  top: 50%;
  right: 10px;
  width: 0;
  height: 0;
  border: 4px solid transparent;
  border-color: #000 transparent transparent transparent;
}

.dark .selected:after {
  border-color: #fff transparent transparent transparent;
}

.items {
  border-radius: 0px 0px 6px 6px;
  overflow-y: auto;
  overflow-x: hidden;
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  transform: translate3d(0, 100%, 0);
}

.items .item {
  cursor: pointer;
  user-select: none;
}

.v-popper__arrow-container {
  display: none;
}
</style>
